/* Homesmart interactive UK house — local, self-contained WebGL; no autoplay.
   Hotspots belong to exact 3D world coordinates and are depth-tested against
   the building in the GPU picking buffer on every user-controlled move. */
const hsBoot=(() => {
  'use strict';
  const house=document.getElementById('house');
  const stage=document.getElementById('houseStage');
  const mount=document.getElementById('house3d');
  const pins=document.getElementById('housePins');
  const controls=document.getElementById('houseControls');
  const legend=document.getElementById('houseLegend');
  if(!house||!stage||!mount||!pins)return;
  const canvas=document.createElement('canvas');
  canvas.tabIndex=0;
  canvas.setAttribute('role','application');
  canvas.setAttribute('aria-label','Interactive British house model. Drag to rotate, pinch or use plus and minus to zoom, arrow keys to rotate, or use the product links below.');
  mount.appendChild(canvas);
  const gl=canvas.getContext('webgl',{alpha:true,antialias:true,powerPreference:'low-power',preserveDrawingBuffer:false});
  if(!gl){canvas.remove();return;}
  try {
    function shader(type,source){const x=gl.createShader(type);gl.shaderSource(x,source);gl.compileShader(x);if(!gl.getShaderParameter(x,gl.COMPILE_STATUS))throw Error(gl.getShaderInfoLog(x));return x;}
    function program(vs,fs){const p=gl.createProgram();gl.attachShader(p,shader(gl.VERTEX_SHADER,vs));gl.attachShader(p,shader(gl.FRAGMENT_SHADER,fs));gl.linkProgram(p);if(!gl.getProgramParameter(p,gl.LINK_STATUS))throw Error(gl.getProgramInfoLog(p));return p;}
    const meshProgram=program(`
      attribute vec3 aPosition; attribute vec3 aNormal; uniform mat4 uMVP;
      varying float vLight; void main(){
        vec3 n=normalize(aNormal);
        float key=max(dot(n,normalize(vec3(-.6,-.9,1.6))),0.0);
        float fill=max(dot(n,normalize(vec3(.4,.7,.65))),0.0);
        vLight=.67+.28*key+.10*fill;
        gl_Position=uMVP*vec4(aPosition,1.0);
      }`, `precision mediump float;
      uniform vec3 uColor;uniform bool uPicking;uniform vec3 uPickColor;varying float vLight;
      void main(){gl_FragColor=vec4(uPicking?uPickColor:min(vec3(1.0),uColor*vLight),1.0);}`);
    const markerProgram=program(`
      attribute vec3 aPosition;uniform mat4 uMVP;
      void main(){gl_Position=uMVP*vec4(aPosition,1.0);gl_PointSize=7.0;}
    `,`precision mediump float;uniform float uIndex;
      void main(){gl_FragColor=vec4(uIndex/255.0,0.0,0.0,1.0);}`);
    const M={p:meshProgram, position:gl.getAttribLocation(meshProgram,'aPosition'),normal:gl.getAttribLocation(meshProgram,'aNormal'),mvp:gl.getUniformLocation(meshProgram,'uMVP'),color:gl.getUniformLocation(meshProgram,'uColor'),picking:gl.getUniformLocation(meshProgram,'uPicking'),pickColor:gl.getUniformLocation(meshProgram,'uPickColor')};
    const B={p:markerProgram,position:gl.getAttribLocation(markerProgram,'aPosition'),mvp:gl.getUniformLocation(markerProgram,'uMVP'),index:gl.getUniformLocation(markerProgram,'uIndex')};
    gl.enable(gl.DEPTH_TEST);gl.depthFunc(gl.LEQUAL);gl.disable(gl.CULL_FACE);

    const bytes=window.__hsGLB;
    const dv=new DataView(bytes.buffer);
    if(dv.getUint32(0,true)!==0x46546c67)throw Error('Invalid GLB');
    const jsonLen=dv.getUint32(12,true);
    const doc=JSON.parse(new TextDecoder().decode(bytes.subarray(20,20+jsonLen)));
    const start=20+jsonLen+8;
    function acc(index){
      const a=doc.accessors[index],v=doc.bufferViews[a.bufferView];
      const off=start+(v.byteOffset||0)+(a.byteOffset||0);
      const len=a.count*({SCALAR:1,VEC2:2,VEC3:3,VEC4:4}[a.type]);
      if(a.componentType===5126)return new Float32Array(bytes.buffer,off,len);
      if(a.componentType===5125)return new Uint32Array(bytes.buffer,off,len);
      if(a.componentType===5123)return new Uint16Array(bytes.buffer,off,len);
      if(a.componentType===5121)return new Uint8Array(bytes.buffer,off,len);
      throw Error('Unsupported mesh accessor '+a.componentType);
    }
    function rankFor(s){
      const categories={Soffit_Vents:2,UPVC_Angle_Trim:3,Downpipe_Connector:48,Airbrick_Vent:23,Over_Fascia_Vents:49};
      return categories[s]||0;
    }
    const vertices=[],draws=[];
    for(const n of doc.nodes){
      if(n.mesh===undefined)continue;
      const mesh=doc.meshes[n.mesh];
      for(const prim of mesh.primitives){
        const pos=acc(prim.attributes.POSITION),idx=prim.indices!==undefined?acc(prim.indices):null;
        const count=idx?idx.length:pos.length/3;
        const first=vertices.length/6;
        for(let k=0;k<count;k+=3){
          const ids=[0,1,2].map(i=>idx?idx[k+i]:k+i);
          const p=ids.map(j=>[pos[j*3],pos[j*3+1],pos[j*3+2]]);
          const u=p[1].map((v,i)=>v-p[0][i]),v=p[2].map((q,i)=>q-p[0][i]);
          let nx=u[1]*v[2]-u[2]*v[1],ny=u[2]*v[0]-u[0]*v[2],nz=u[0]*v[1]-u[1]*v[0];
          const len=Math.hypot(nx,ny,nz)||1;
          for(const q of p)vertices.push(...q,nx/len,ny/len,nz/len);
        }
        const mat=doc.materials[prim.material]||{};
        const color=mat.pbrMetallicRoughness?.baseColorFactor||[.8,.8,.8,1];
        draws.push({first,count,color,rank:rankFor(n.name||''),name:n.name||''});
      }
    }
    const buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array(vertices),gl.STATIC_DRAW);
    const locations=[
      {rank:2,label:'70mm Round Soffit Vents',point:[-2.64,-2.79,3.452]},
      {rank:49,label:'Over Fascia Vents',point:[-.12,-2.788,3.90]},
      {rank:3,label:'uPVC Window Angle Trim',point:[1.005,-2.665,3.00]},
      {rank:48,label:'Round Downpipe Straight Connector',point:[3.445,-2.85,2.16]},
      {rank:23,label:'Hooded Cowl Vent Cover',point:[-2.74,-2.91,.88]},
      {rank:23,label:'Rear Cowl Vent Cover',point:[2.48,2.91,.88]},
      {rank:48,label:'Rear Round Downpipe Connector',point:[-3.445,2.85,2.16]}
    ];
    const landmarks=locations.map((o,i)=>{
      const a=document.createElement('a');
      a.href=(window.HSurl?window.HSurl(o.rank):'#');a.dataset.rank=o.rank;a.className='spot'+(o.point[0]>2.7?' spot--l':'');
      a.setAttribute('aria-label','View '+o.label);
      a.innerHTML='<i aria-hidden="true"></i><span class="tip"><b>'+o.label+'</b><span>View product details →</span></span>';
      pins.appendChild(a);
      return {...o,id:230+i,a};
    });
    const coords=new Float32Array(landmarks.flatMap(x=>x.point));
    const markerBuffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,markerBuffer);gl.bufferData(gl.ARRAY_BUFFER,coords,gl.STATIC_DRAW);

    function dot(a,b){return a[0]*b[0]+a[1]*b[1]+a[2]*b[2]}
    function sub(a,b){return [a[0]-b[0],a[1]-b[1],a[2]-b[2]]}
    function cross(a,b){return [a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]]}
    function norm(v){const n=Math.hypot(...v)||1;return v.map(x=>x/n)}
    function perspective(fov,aspect,near,far){const f=1/Math.tan(fov/2),d=1/(near-far);return [f/aspect,0,0,0,0,f,0,0,0,0,(far+near)*d,-1,0,0,2*far*near*d,0]}
    function lookAt(eye,target,up){const z=norm(sub(eye,target)),x=norm(cross(up,z)),y=cross(z,x);return [x[0],y[0],z[0],0,x[1],y[1],z[1],0,x[2],y[2],z[2],0,-dot(x,eye),-dot(y,eye),-dot(z,eye),1]}
    function multiply(a,b){const m=new Float32Array(16);for(let c=0;c<4;c++)for(let r=0;r<4;r++)for(let j=0;j<4;j++)m[c*4+r]+=a[j*4+r]*b[c*4+j];return m}
    function transform(m,p){const v=[...p,1];return [0,1,2,3].map(r=>m[r]*v[0]+m[r+4]*v[1]+m[r+8]*v[2]+m[r+12]*v[3])}
    const center=[0,0,2.62],initial={az:Math.atan2(-10,8),el:.265};
    const initialAngle=initial.az;
    let az=initial.az,el=initial.el,mvp;
    // Fit the same entire house to both the wide desktop panel and a portrait phone.
    function fitDistance(){
      const ratio=Math.max(.58,stage.clientWidth/Math.max(1,stage.clientHeight));
      return 17.3*Math.max(1,1.28/ratio);
    }
    let fit=fitDistance(),dist=fit;
    function minDistance(){return fit*.56}
    function maxDistance(){return fit*1.75}
    let fbo,texture,depth,needResize=true;
    let scheduled=0;
    function requestDraw(){
      if(scheduled||document.hidden||false)return;
      scheduled=requestAnimationFrame(()=>{scheduled=0;draw()});
    }
    function resize(){
      // A bounded render target avoids excessive GPU allocations on high-DPI phones.
      const touchDevice=matchMedia('(pointer:coarse)').matches;
      const dprCap=touchDevice?1.35:1.75;
      const dpr=Math.min(window.devicePixelRatio||1,dprCap);
      const pixelBudget=touchDevice?650000:1700000;
      const scale=Math.min(dpr,Math.sqrt(pixelBudget/Math.max(1,mount.clientWidth*mount.clientHeight)));
      const w=Math.max(1,Math.round(mount.clientWidth*scale)),h=Math.max(1,Math.round(mount.clientHeight*scale));
      if(canvas.width===w&&canvas.height===h&&!needResize)return;
      canvas.width=w;canvas.height=h;needResize=false;
      if(texture)gl.deleteTexture(texture);if(depth)gl.deleteRenderbuffer(depth);if(fbo)gl.deleteFramebuffer(fbo);
      texture=gl.createTexture();gl.bindTexture(gl.TEXTURE_2D,texture);
      gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,w,h,0,gl.RGBA,gl.UNSIGNED_BYTE,null);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.NEAREST);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.NEAREST);
      depth=gl.createRenderbuffer();gl.bindRenderbuffer(gl.RENDERBUFFER,depth);
      gl.renderbufferStorage(gl.RENDERBUFFER,gl.DEPTH_COMPONENT16,w,h);
      fbo=gl.createFramebuffer();gl.bindFramebuffer(gl.FRAMEBUFFER,fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER,gl.COLOR_ATTACHMENT0,gl.TEXTURE_2D,texture,0);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER,gl.DEPTH_ATTACHMENT,gl.RENDERBUFFER,depth);
      if(gl.checkFramebufferStatus(gl.FRAMEBUFFER)!==gl.FRAMEBUFFER_COMPLETE)throw Error('WebGL selection framebuffer incomplete');
      gl.bindFramebuffer(gl.FRAMEBUFFER,null);
    }
    function projection(){
      const eye=[center[0]+dist*Math.cos(el)*Math.cos(az),center[1]+dist*Math.cos(el)*Math.sin(az),center[2]+dist*Math.sin(el)];
      return multiply(perspective(40*Math.PI/180,canvas.width/canvas.height,.08,100),lookAt(eye,center,[0,0,1]));
    }
    function renderMesh(picking){
      gl.useProgram(M.p);
      gl.bindBuffer(gl.ARRAY_BUFFER,buffer);
      gl.enableVertexAttribArray(M.position);gl.vertexAttribPointer(M.position,3,gl.FLOAT,false,24,0);
      gl.enableVertexAttribArray(M.normal);gl.vertexAttribPointer(M.normal,3,gl.FLOAT,false,24,12);
      gl.uniformMatrix4fv(M.mvp,false,mvp);gl.uniform1i(M.picking,picking?1:0);
      for(const d of draws){
        gl.uniform3f(M.color,d.color[0],d.color[1],d.color[2]);
        gl.uniform3f(M.pickColor,d.rank/255,0,0);
        gl.drawArrays(gl.TRIANGLES,d.first,d.count);
      }
    }
    function markDepth(){
      gl.useProgram(B.p);gl.bindBuffer(gl.ARRAY_BUFFER,markerBuffer);
      gl.enableVertexAttribArray(B.position);gl.vertexAttribPointer(B.position,3,gl.FLOAT,false,12,0);
      gl.uniformMatrix4fv(B.mvp,false,mvp);
      for(let i=0;i<landmarks.length;i++){
        gl.uniform1f(B.index,landmarks[i].id);
        gl.drawArrays(gl.POINTS,i,1);
      }
    }
    function pickBuffer(withMarkers){
      gl.bindFramebuffer(gl.FRAMEBUFFER,fbo);
      gl.viewport(0,0,canvas.width,canvas.height);
      gl.disable(gl.BLEND);gl.disable(gl.DITHER);
      gl.clearColor(0,0,0,1);gl.clear(gl.COLOR_BUFFER_BIT|gl.DEPTH_BUFFER_BIT);
      renderMesh(true);
      if(withMarkers)markDepth();
    }
    function sample(pixelX,pixelY){
      const sample=new Uint8Array(4);
      gl.readPixels(Math.max(0,Math.min(canvas.width-1,pixelX)),Math.max(0,Math.min(canvas.height-1,pixelY)),1,1,gl.RGBA,gl.UNSIGNED_BYTE,sample);
      return sample[0];
    }
    function draw(){
      resize();mvp=projection();
      gl.bindFramebuffer(gl.FRAMEBUFFER,null);gl.viewport(0,0,canvas.width,canvas.height);
      gl.clearColor(0,0,0,0);gl.clear(gl.COLOR_BUFFER_BIT|gl.DEPTH_BUFFER_BIT);renderMesh(false);
      pickBuffer(true);
      for(const marker of landmarks){
        const v=transform(mvp,marker.point),w=v[3];
        const x=v[0]/w,y=v[1]/w,z=v[2]/w;
        let shown=w>0&&Math.abs(x)<.90&&Math.abs(y)<.88&&z>-1&&z<1;
        if(shown){
          const px=Math.round((x+1)*.5*canvas.width),py=Math.round((y+1)*.5*canvas.height);
          shown=sample(px,py)===marker.id;
        }
        marker.a.hidden=!shown;
        if(shown){marker.a.style.left=((x+1)*50)+'%';marker.a.style.top=((1-y)*50)+'%';}
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER,null);
    }
    function hitAt(x,y){
      const r=canvas.getBoundingClientRect();
      if(!r.width||!r.height)return 0;
      pickBuffer(false);
      const px=Math.floor((x-r.left)/r.width*canvas.width);
      const py=Math.floor((r.bottom-y)/r.height*canvas.height);
      const rank=sample(px,py);gl.bindFramebuffer(gl.FRAMEBUFFER,null);
      return rank>0&&rank<=50?rank:0;
    }
    function clamp(v,min,max){return Math.max(min,Math.min(max,v))}
    function orbit(dx,dy,startAz,startEl){
      az=startAz-dx/Math.max(180,mount.clientWidth)*3.1;
      el=clamp(startEl+dy/Math.max(170,mount.clientHeight)*1.6,-.25,1.36);
      requestDraw();
    }
    function zoom(mult){dist=clamp(dist*mult,minDistance(),maxDistance());requestDraw()}
    document.getElementById('houseZoomIn').addEventListener('click',()=>zoom(.84));
    document.getElementById('houseZoomOut').addEventListener('click',()=>zoom(1.19));
    document.getElementById('houseReset').addEventListener('click',()=>{
      az=initialAngle;el=initial.el;fit=fitDistance();dist=fit;requestDraw();
    });
    mount.addEventListener('wheel',e=>{e.preventDefault();zoom(Math.exp(clamp(e.deltaY,-180,180)*.001));},{passive:false});
    const points=new Map();let drag=null,pinch=null,gestureHadPinch=false;
    function startGesture(){
      const q=[...points.values()];
      if(q.length===1){drag={x:q[0].x,y:q[0].y,az,el,moved:false};pinch=null;}
      else if(q.length>=2){gestureHadPinch=true;drag=null;const a=q[0],b=q[1];pinch={distance:Math.hypot(a.x-b.x,a.y-b.y)||1,dist};}
    }
    mount.addEventListener('pointerdown',e=>{
      if(e.pointerType==='mouse'&&e.button!==0)return;
      if(points.size===0)gestureHadPinch=false;
      points.set(e.pointerId,{x:e.clientX,y:e.clientY});
      mount.setPointerCapture(e.pointerId);startGesture();
    });
    mount.addEventListener('pointermove',e=>{
      if(!points.has(e.pointerId))return;
      points.set(e.pointerId,{x:e.clientX,y:e.clientY});
      if(points.size>=2&&pinch){
        const [a,b]=[...points.values()];
        const now=Math.max(12,Math.hypot(a.x-b.x,a.y-b.y));
        dist=clamp(pinch.dist*pinch.distance/now,minDistance(),maxDistance());requestDraw();
      }else if(points.size===1&&drag){
        const dx=e.clientX-drag.x,dy=e.clientY-drag.y;
        if(Math.hypot(dx,dy)>5)drag.moved=true;
        if(drag.moved)orbit(dx,dy,drag.az,drag.el);
      }
    });
    function pointerEnd(e){
      if(!points.has(e.pointerId))return;
      const wasTap=points.size===1&&drag&&!drag.moved&&!gestureHadPinch&&Math.hypot(e.clientX-drag.x,e.clientY-drag.y)<9;
      points.delete(e.pointerId);startGesture();
      if(wasTap){
        // Complete any pending camera update before identifying the tapped object.
        if(scheduled){cancelAnimationFrame(scheduled);scheduled=0;draw();}
        const rank=hitAt(e.clientX,e.clientY);
        if(rank)location.href=(window.HSurl?window.HSurl(rank):'#');
      }
    }
    mount.addEventListener('pointerup',pointerEnd);
    mount.addEventListener('pointercancel',e=>{points.delete(e.pointerId);startGesture();if(points.size===0)gestureHadPinch=false});
    canvas.addEventListener('keydown',e=>{
      const keys=['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','+','-','='];
      if(!keys.includes(e.key))return;e.preventDefault();
      if(e.key==='ArrowLeft')az-=.17;
      if(e.key==='ArrowRight')az+=.17;
      if(e.key==='ArrowUp')el=clamp(el+.1,-.25,1.36);
      if(e.key==='ArrowDown')el=clamp(el-.1,-.25,1.36);
      if(e.key==='+'||e.key==='=')dist=clamp(dist*.88,minDistance(),maxDistance());
      if(e.key==='-')dist=clamp(dist*1.12,minDistance(),maxDistance());
      requestDraw();
    });
    const ro=new ResizeObserver(()=>{
      const nextFit=fitDistance();
      if(Math.abs(fit-nextFit)>.01){dist=clamp(dist*nextFit/fit,nextFit*.56,nextFit*1.75);fit=nextFit;}
      needResize=true;requestDraw();
    });
    ro.observe(stage);
    window.addEventListener('orientationchange',()=>{needResize=true;requestDraw()});
    window.addEventListener('hashchange',()=>requestDraw());
    document.addEventListener('visibilitychange',()=>{if(!document.hidden)requestDraw()});
    canvas.addEventListener('webglcontextlost',e=>{
      e.preventDefault();house.classList.remove('three-ready');
      console.warn('3D graphics paused; showing the accessible house illustration.');
    });
    house.classList.add('three-ready');controls.hidden=false;legend.hidden=false;
    const cap=document.getElementById('houseCaption');
    cap.textContent='Drag to explore · Pinch or use + / − to zoom · Tap a product to shop';
    draw();
    window.Homesmart3D={
      get modelParts(){return draws.length},
      get hasWebGL(){return !!gl},
      get version(){return 'UK architectural home · mobile-first manual controls'},
      get azimuth(){return az},get elevation(){return el},get distance(){return dist},
      get visiblePins(){return landmarks.filter(x=>!x.a.hidden).map(x=>x.label)},
      get renderedSize(){return [canvas.width,canvas.height]},
      get stageSize(){return [mount.clientWidth,mount.clientHeight]},
      get fitDistance(){return fit},
      rotate:(radians)=>{az+=radians;draw()},
      reset:()=>document.getElementById('houseReset').click()
    };
  }catch(err){
    console.error('3D house initialisation failed. Using original SVG illustration.',err);
    canvas.remove();
  }
});
(function(){var h=document.getElementById('house');if(!h||!h.dataset.glb)return;
fetch(h.dataset.glb).then(function(r){if(!r.ok)throw Error('GLB '+r.status);return r.arrayBuffer()}).then(function(b){window.__hsGLB=new Uint8Array(b);hsBoot()}).catch(function(e){console.warn('3D house unavailable; showing illustration.',e)});})();
